/* @jsx h */
import { defineComponent, h } from "vue";
import { RouterLink } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import type { MenuEngineering, Quadrant } from "./insights";

type Props = { engineering: MenuEngineering };

// Laid out like the classic chart: margin up, popularity right.
const CELLS: { quadrant: Quadrant; icon: string; tone: string }[] = [
  { quadrant: "puzzle", icon: "extension", tone: "bg-info/10 text-info" },
  { quadrant: "star", icon: "star", tone: "bg-success/10 text-success" },
  { quadrant: "dog", icon: "pets", tone: "bg-error/10 text-error" },
  { quadrant: "plowhorse", icon: "agriculture", tone: "bg-warning/15 text-warning" },
];

// Menu engineering: which dishes to keep, promote, re-price or drop.
export const MenuMatrix = defineComponent<Props>({
  name: "MenuMatrix",
  props: ["engineering"],
  setup(props) {
    const { t } = useLocalization();

    const empty = () => (
      <div class="flex flex-col items-center gap-2 rounded-lg bg-base-200 p-6 text-center">
        <span class="material-symbols-outlined text-3xl text-base-content/30">grid_view</span>
        <p class="max-w-sm text-sm text-base-content/70">{t("catalog.overview.matrix_empty")}</p>
        {props.engineering.uncosted > 0 && (
          <p class="text-xs text-base-content/50">
            {t("catalog.overview.matrix_uncosted").replace("{count}", String(props.engineering.uncosted))}
          </p>
        )}
        <RouterLink to="/app/catalog_editor/products" class="btn btn-primary btn-sm mt-1">
          <span class="material-symbols-outlined text-base">receipt_long</span>
          {t("catalog.overview.action_add_recipes")}
        </RouterLink>
      </div>
    );

    return () => {
      const { items, uncosted } = props.engineering;
      if (!items.length) return empty();
      return (
        <div>
          <div class="flex gap-2">
            <div class="flex w-4 items-center justify-center">
              <span class="-rotate-90 whitespace-nowrap text-[10px] uppercase tracking-wide text-base-content/40">
                {t("catalog.overview.axis_margin")} →
              </span>
            </div>
            <div class="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
              {CELLS.map((cell) => {
                const inCell = items
                  .filter((i) => i.quadrant === cell.quadrant)
                  .sort((a, b) => b.quantity - a.quantity);
                return (
                  <div key={cell.quadrant} class="rounded-lg border border-base-300 p-3">
                    <div class="flex items-center gap-2">
                      <span class={`flex h-7 w-7 items-center justify-center rounded-full ${cell.tone}`}>
                        <span class="material-symbols-outlined text-base">{cell.icon}</span>
                      </span>
                      <span class="text-sm font-semibold text-accent">{t(`catalog.overview.q_${cell.quadrant}`)}</span>
                      <span class="ml-auto text-lg font-semibold tabular-nums text-accent">{inCell.length}</span>
                    </div>
                    <p class="mt-1 text-xs text-base-content/60">{t(`catalog.overview.q_${cell.quadrant}_hint`)}</p>
                    {inCell.length > 0 && (
                      <ul class="mt-2 space-y-0.5 text-xs">
                        {inCell.slice(0, 3).map((i) => (
                          <li key={i.id} class="flex justify-between gap-2">
                            <span class="truncate text-accent/90">{i.name}</span>
                            <span class="whitespace-nowrap tabular-nums text-base-content/50">
                              {`${i.quantity} · ${i.marginPct.toFixed(0)}%`}
                            </span>
                          </li>
                        ))}
                        {inCell.length > 3 && (
                          <li class="text-base-content/40">
                            {t("catalog.overview.and_more").replace("{count}", String(inCell.length - 3))}
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div class="mt-1 pl-6 text-center text-[10px] uppercase tracking-wide text-base-content/40">
            {t("catalog.overview.axis_popularity")} →
          </div>
          {uncosted > 0 && (
            <p class="mt-2 text-xs text-base-content/50">
              {t("catalog.overview.matrix_uncosted").replace("{count}", String(uncosted))}
            </p>
          )}
        </div>
      );
    };
  },
});
